import { Employee } from "@finway-group/shared/lib/models"
import { Button, Form, Modal } from "antd"
import { Store } from "antd/lib/form/interface"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import EmployeeCreditorNumberForm from "~Components/forms/employeeCreditorNumber.form"
import { EmployeeService, NotificationService } from "~Shared/services"
import DialogService from "~Shared/services/dialog.service"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import { patchCompanyProfile } from "~Shared/store/actions/employee/employeeActions"
import { isEmptyString, validateCreditorNumberDATEVCompatibility } from "~Shared/utils/helper.utils"
import useStateIfMounted from "~Shared/utils/hooks/useStateIfMounted"

const { confirm } = Modal

interface EmployeeCreditorNumberModalProps {
    isShowing: boolean
    employee: Employee
    onCancel: () => void
    onCreditorNumberChangeSuccess?: () => Promise<void>
}

const EmployeeCreditorNumberModal = ({ employee, isShowing, onCancel, onCreditorNumberChangeSuccess }: EmployeeCreditorNumberModalProps) => {
    const { t } = useTranslation()
    const [formInstance] = Form.useForm()
    const [isLoading, setIsLoading] = useStateIfMounted(false)
    const [shouldSyncExpenses, setShouldSyncExpenses] = useStateIfMounted(false)

    const { activeCompanyProfile } = employee

    const dispatch = useDispatch()

    useEffect(() => {
        let isMounted = true

        if (isShowing) {
            formInstance.setFieldsValue({ companyProfile: activeCompanyProfile })
            setIsLoading(true)
            EmployeeService.getLatestEmployeeCreditorNumber()
                .then((creditorNumber) => {
                    if (isMounted) {
                        formInstance.setFieldsValue({ creditorNumber })
                    }
                })
                .finally(() => setIsLoading(false))
        }

        return () => {
            isMounted = false
        }
    }, [isShowing])

    const onSubmit = async (values: Store, forceSubmit?: boolean) => {
        let shouldSynchronizeExpenses = shouldSyncExpenses

        if (!forceSubmit) {
            try {
                validateCreditorNumberDATEVCompatibility(values.creditorNumber)
            } catch (err) {
                confirm({
                    title: t("confirm:vendor.incompatible_creditor_number.title"),
                    content: t(`confirm:vendor.incompatible_creditor_number.message`, { reason: err.message }),
                    cancelText: t("confirm:vendor.incompatible_creditor_number.cancel"),
                    type: "warning",
                    okText: t("confirm:vendor.incompatible_creditor_number.confirm"),
                    onOk() {
                        return onSubmit(values, true)
                    },
                })
                return
            }
        }

        if (!shouldSynchronizeExpenses && (await DialogService.confirmSync())) {
            shouldSynchronizeExpenses = true
        }
        try {
            setIsLoading(true)
            const creditorNumber = !isEmptyString(values.creditorNumber) ? values.creditorNumber : null

            await patchCompanyProfile(employee.id, activeCompanyProfile.companyId, { creditorNumber })(dispatch)
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:employee.updated.title"), t("notification:employee.updated.message"))
            await onCreditorNumberChangeSuccess?.()
            onHide()
        } catch (err) {
            setIsLoading(false)
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:employee.edit.title"))
        }
    }

    const onHide = () => {
        setIsLoading(false)
        onCancel()
    }

    return (
        <Modal
            title={t("action:employee.add_creditor_number_name", { name: employee.getFullName() })}
            open={isShowing}
            maskClosable={false}
            onCancel={onHide}
            footer={[
                <Button key="1" onClick={onHide}>
                    {t("action:cancel")}
                </Button>,
                <Button key="2" type="primary" loading={isLoading} onClick={() => formInstance.submit()}>
                    {t("action:save")}
                </Button>,
            ]}
            confirmLoading={isLoading}
            keyboard
            destroyOnClose
        >
            <EmployeeCreditorNumberForm formInstance={formInstance} onSubmit={onSubmit} />
        </Modal>
    )
}

export default EmployeeCreditorNumberModal
